import spartaStoreUtility from "utilities/global/sparta-store-utility/3.1.1/js/sparta-store-utility";
import textMask from "utilities/vendor/text-mask/2.0.0/js/index";
/* global spartaStoreUtility textMask */

/**
 * @name        Sparta Input Formatting Utility
 * @file        sparta-input-formatting-utility.js
 * @description global wrapper for the vendor/text-mask utility
 * @classdesc   global wrapper for the vendor/text-mask utility
 * @global      Creates a global SpartaInputFormattingUtility object
 * @author      zk5j3fu
 * @copyright   © 2017 Bank of America Corporation. All rights reserved.
 */

// Takes an element and index
// moves the cursor to a specific place
const setCaretPosition = (elem, caretPos) => {
  if (elem.createTextRange) {
    const range = elem.createTextRange();
    range.move('character', caretPos);
    range.select();
  } else {
    elem.focus();
    if (elem.selectionStart) {
      elem.setSelectionRange(caretPos, caretPos);
    }
  }
};

// uses indices to grab a section of a string
const getChunk = (value, indices) => value.slice(indices.start, indices.end);
// iterates over an array of arrays
// the inner layer contains indices for the start and end position for str.splice
const splitChunk = (format, value) => format.map(indices => getChunk(value, indices));
// removes everything but digits and calls functions to split up value into proper format
const processFormat = (input, formatSplit) => {
  const currentValue = input.value.replace(/\D/g, '');
  return splitChunk(formatSplit, currentValue).filter(subString => subString.length > 0);
};

class Formatter {
  constructor(input) {
    this.input = input;
    this.inputValue = input.value;
    // oldValue will store the previous value for reference
    this.oldValue = this.inputValue;
    this.valueLength = this.inputValue.length;
    // true/false value
    // true = the user added a digit
    // false = the user removed a digit
    this.addedValue = null;
    // the seperator to join string chunks
    this.joiner = '-';
    // array of objects to indicate indices for start and end position of str.split
    this.formatSplit = [];
  }
  attachEvent() {
    this.input.addEventListener('input', (event) => {
      this.event = event;
      this.registerChange();
      setTimeout(() => {
        this.processChange();
      }, 0);
    });
    return this;
  }
  registerChange() {
    const newValue = processFormat(this.input, this.formatSplit).join(this.joiner);
    this.updateValueInMemory(newValue);
    return this;
  }
  updateValueInMemory(newValue) {
    const newLength = newValue.length;
    this.addedValue = this.valueLength < newLength;
    this.caretChange = newLength - this.valueLength;
    this.oldValue = this.inputValue;
    this.inputValue = newValue;
    this.valueLength = newLength;
    return this;
  }
  processCaretChange() {
    // where the cursor is before format
    const startPosition = this.input.selectionStart;
    // if the cursor is not at the end
    const needsUpdate = this.valueLength > startPosition;

    if (needsUpdate) {
      const rawValue = this.inputValue.replace(/\D/g, '');
      const oldRawValue = this.oldValue.replace(/\D/g, '');
      const illegalAddition = rawValue.length === oldRawValue.length;
      // keeps cursor position from being set to -1
      const caretChange = this.caretChange > 0 ? this.caretChange : 0;
      // where the new position will be
      const caretPos = startPosition + caretChange;
      // is the new position inside what is being input?
      if (caretPos <= this.valueLength) {
        // did the user add a digit?
        if (this.addedValue) {
          if (!this.joiner.includes(this.inputValue[startPosition - 1])) {
            // put them where they were
            return startPosition;
          }
        } else if (illegalAddition) {
          // if they did not enter a digit
          // dont move them
          if (this.joiner.includes(this.inputValue[startPosition])) {
            return startPosition;
          }
          return startPosition - 1;
        }
        // put them where they should be
        return caretPos;
      }
    }
    return false;
  }

  processChange() {
    const caretPos = this.processCaretChange();
    this.input.value = this.inputValue;
    if (caretPos !== false) {
      setCaretPosition(this.input, caretPos);
    }
    return this;
  }
}

class DateFormat extends Formatter {
  constructor(input) {
    super(input);
    this.joiner = '/';
    this.formatSplit = [{
      start: 0,
      end: 2,
    }, {
      start: 2,
      end: 4,
    }, {
      start: 4,
      end: 8,
    }];
  }
}

class MonthYearDateFormat extends Formatter {
  constructor(input) {
    super(input);
    this.joiner = '/';
    this.formatSplit = [{
      start: 0,
      end: 2,
    }, {
      start: 2,
      end: 6,
    }];
  }
}

class ZipFormat extends Formatter {
  constructor(input) {
    super(input);
    this.joiner = '';
    this.formatSplit = [{
      start: 0,
      end: 6,
    }];
  }
}

class SsnFormat extends Formatter {
  constructor(input) {
    super(input);
    this.formatSplit = [{
      start: 0,
      end: 3,
    }, {
      start: 3,
      end: 5,
    }, {
      start: 5,
      end: 9,
    }];
  }
}

class PhoneFormat extends Formatter {
  constructor(input) {
    super(input);
    this.formatSplit = [{
      start: 0,
      end: 3,
    }, {
      start: 3,
      end: 6,
    }, {
      start: 6,
      end: 10,
    }];
  }
}

class ZipExtendedFormat extends Formatter {
  constructor(input) {
    super(input);
    this.formatSplit = [{
      start: 0,
      end: 5,
    }, {
      start: 5,
      end: 9,
    }];
  }
}

class CcNumberFormat extends Formatter {
  constructor(input) {
    super(input);
    this.formatSplit = [{
      start: 0,
      end: 4,
    }, {
      start: 4,
      end: 8,
    }, {
      start: 8,
      end: 12,
    }, {
      start: 12,
      end: 16,
    }];
  }
}

class EinFormat extends Formatter {
  constructor(input) {
    super(input);
    this.formatSplit = [{
      start: 0,
      end: 2,
    }, {
      start: 2,
      end: 9,
    }];
  }
}

class PhoneExtendedFormat extends Formatter {
  constructor(input) {
    super(input);
    this.joiner = '- x';
    this.formatSplit = [{
      start: 0,
      end: 3,
    }, {
      start: 3,
      end: 6,
    }, {
      start: 6,
      end: 10,
    }, {
      start: 10,
      end: 3,
    }];
  }
  updateString() {
    const value = processFormat(this.input, this.formatSplit).join('-').split('');
    value.split('')[12] = ' x';
    return value.join('');
  }
  registerChange() {
    const newValue = this.updateString();
    this.updateValueInMemory(newValue);
  }
}

const formatMap = {
  date: input => new DateFormat(input),
  ssn: input => new SsnFormat(input),
  phone: input => new PhoneFormat(input),
  ein: input => new EinFormat(input),
  zip: input => new ZipFormat(input),
  'zip-extended': input => new ZipExtendedFormat(input),
  'month-year-date': input => new MonthYearDateFormat(input),
  'cc-number': input => new CcNumberFormat(input),
  'phone-extended': input => new PhoneExtendedFormat(input),
};

/**
 * @method formatByClass
 * @description formatting method as a fallback for android
 * @param {Object} input input element
 * @param {String} format type of input to determine formatting
 */
const formatByClass = (item, formatMethod, integrations) => {
  if (formatMap[item.format] && integrations.indexOf(item.format) > -1) {
    formatMap[item.format](item.input).attachEvent();
    return true;
  }
  formatMethod(item.input, item.format);
  return false;
};

/**
 * @method getInputs
 * @description get input element and formatting type
 */
const getInputs = (formats) => {
  // grab all inputs with a data format attribute
  const inputs = [].slice.call(document.querySelectorAll('[data-sparta-input-format]'));

  // grab all formatting types and store them in an array
  return inputs.map((item) => {
    const inputFormat = item.getAttribute('data-sparta-input-format');
    const isSupported = formatMap[inputFormat] || formats[inputFormat];
    // store supported inputs only
    if (isSupported && !item.hasAttribute('data-sparta-input-format-initialized')) {
      item.setAttribute('data-sparta-input-format-initialized', true);
      return ({
        input: item,
        format: inputFormat,
        listener: false,
      });
    }
    return undefined;
  }).filter(item => item);
};

// TODO [Joshua Smith]
// As we transition to a holistic solution for input formatting
// there need to be only certain inputs and browsers that are
// integrated at a time, in order to curb the possible negative
// side effects of switching.
const inputIntegrations = {
  android: [
    'ssn',
    'zip-extended',
    'ein',
    'phone',
    'cc-number',
    'date',
    'month-year-date',
  ],
  msie: [
    'zip-extended',
  ],
};

class SpartaInputFormattingUtility {
  constructor() {
    this.formats = {
      'cc-number': [/\d/, /\d/, /\d/, /\d/, '-', /\d/, /\d/, /\d/, /\d/, '-', /\d/, /\d/, /\d/, /\d/, '-', /\d/, /\d/, /\d/, /\d/],
      currency: textMask.createNumberMask({
        prefix: '',
        allowDecimal: true,
      }),
      'currency-integer': textMask.createNumberMask({
        prefix: '',
      }),
      date: [/\d/, /\d/, '/', /\d/, /\d/, '/', /\d/, /\d/, /\d/, /\d/],
      'month-year-date': [/\d/, /\d/, '/', /\d/, /\d/, /\d/, /\d/],
      numeric: textMask.createNumberMask({
        prefix: '',
      }),
      percent: textMask.createNumberMask({
        suffix: '%',
        prefix: '',
        allowDecimal: true,
      }),
      phone: [/\d/, /\d/, /\d/, '-', /\d/, /\d/, /\d/, '-', /\d/, /\d/, /\d/, /\d/],
      'phone-extended': [/\d/, /\d/, /\d/, '-', /\d/, /\d/, /\d/, '-', /\d/, /\d/, /\d/, /\d/, ' ', 'x', /\d/, /\d/, /\d/, /\d/],
      ssn: [/\d/, /\d/, /\d/, '-', /\d/, /\d/, '-', /\d/, /\d/, /\d/, /\d/],
      ein: [/\d/, /\d/, '-', /\d/, /\d/, /\d/, /\d/, /\d/, /\d/, /\d/],
      vin: [
        /[a-hj-npr-z\d]/i,
        /[a-hj-npr-z\d]/i,
        /[a-hj-npr-z\d]/i,
        /[a-hj-npr-z\d]/i,
        /[a-hj-npr-z\d]/i,
        /[a-hj-npr-z\d]/i,
        /[a-hj-npr-z\d]/i,
        /[a-hj-npr-z\d]/i,
        /\d|X/,
        /[a-hj-npr-z\d]/i,
        /[a-hj-npr-z\d]/i,
        /[a-hj-npr-z\d]/i,
        /[a-hj-npr-z\d]/i,
        /[a-hj-npr-z\d]/i,
        /\d/,
        /\d/,
        /\d/,
      ],
      zip: [/\d/, /\d/, /\d/, /\d/, /\d/],
      'zip-extended': [/\d/, /\d/, /\d/, /\d/, /\d/, '-', /\d/, /\d/, /\d/, /\d/],
    };
    // For VIN validation information
    // https://en.wikipedia.org/wiki/Vehicle_identification_number#World_manufacturer_identifier

    this.defaults = {
      guide: false,
      placeholderChar: '\u2000',
    };

    this.browser = spartaStoreUtility.getData('browser');

    this.onBlurListeners = ['msie', 'ios', 'safari'];

    this.numberTypes = ['currency', 'currency-integer', 'interest-rate',
      'numeric', 'numeric-float', 'percent', 'percent-integer',
    ];

    this.dateTypes = ['date', 'month-year-date'];

    this.inputIntegrations = inputIntegrations;
  }

  /**
   * @method init
   * @description initialized by sparta-input-utility
   * @param {Array} inputs input element containers
   */
  init() {
    // filter and store inputs
    const inputs = getInputs(this.formats);
    if (inputs.length > 0) {
      const useListener = this.onBlurListeners.filter(idx => this.browser[idx]).length;
      const integratedBrowser = Object.keys(this.inputIntegrations)
        .filter(browser => this.browser[browser]);
      const formatMethod = this.formatInput.bind(this);

      inputs.forEach((item, index) => {
        if (integratedBrowser.length) {
          const integrations = this.inputIntegrations[integratedBrowser[0]];
          formatByClass(item, formatMethod, integrations);
        } else {
          formatMethod(item.input, item.format);
        }

        // FIX: IE/Safari issue
        // change events don't get fired by when the input mask is a function.
        if (useListener && typeof this.formats[item.format] === 'function') {
          if (!item.listener) {
            item.input.addEventListener('blur', () => {
              if (item.input.value !== inputs[index].previousValue) {
                const event = document.createEvent('HTMLEvents');
                event.initEvent('change', false, true);
                item.input.dispatchEvent(event);
                inputs[index].previousValue = item.input.value;
              }
            });
          }

          inputs[index].listener = true;
        }
      });
    }
  }

  /**
   * @method formatInput
   * @description initialize formatting
   * @param {Object} input input element
   * @param {String} format type of input to determine formatting
   */
  formatInput(input, format) {
    const settings = {
      ...this.defaults,
      inputElement: input,
      mask: this.formats[format],
    };

    // the date format uses a special pipe filter that is invoked differently
    if (this.dateTypes.indexOf(format) > -1) {
      let dateFormat = 'mm/dd/yyyy';
      if (format === 'month-year-date') {
        dateFormat = 'mm/yyyy';
      }

      const pipe = textMask.createAutoCorrectedDatePipe(dateFormat);
      Object.assign(settings, {
        pipe,
      });
    }

    textMask.maskInput(settings);
  }
}

/**
 * @const SpartaInputFormattingUtility
 * @description single isntance of SpartaInputFormattingUtility used to expose this utility
 */
const spartaInputFormattingUtility = new SpartaInputFormattingUtility();

// export the spartaInputFormattingUtility for use in modules and utilities
export default spartaInputFormattingUtility;
